import Vue from 'vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';

dayjs.extend(utc);
dayjs.extend(advancedFormat);

/**
 * Format time of date time text
 * @param {string} date plain text of date time
 */
export const formatTime = date => {
  return dayjs(date).format('HH:mm');
};

/**
 * Format into nice monthly date time
 * @param {String} date plain text of date time
 */
export const formatDateTime = date => {
  return dayjs(date).format('MM/DD HH:mm');
};

/**
 * Format into full date time format of YYYY/MM/DD
 * @param {String} date plain text of date time
 */
export const formatFullDate = date => {
  return dayjs(date).format('YYYY/MM/DD');
};

/**
 * Format into full date time format of YYYY/MM/DD
 * @param {String} date plain text of date time, assuming it is in UTC
 * @param {Number} offset the offset of the date time
 */
export const formatFullDateTime = (date, offset = 9) => {
  return dayjs(date)
    .utc()
    .add(offset, 'hour')
    .format('YYYY/MM/DD HH:mm');
};

/**
 * Format into full date time format of UTC timestamp
 * @param {String} date plain text of date time
 */
export const formatToUtcTimestamp = date => {
  return dayjs(date)
    .utc()
    .format('x');
};

/**
 * Format language code into descriptive name
 * @param {String} code language code with country, ex: en-US
 */
export const formatLangCode = code => {
  const langToString = code => {
    switch (code) {
      case 'en-US':
        return 'English';
      case 'zh-TW':
        return 'Traditional Chinese';
      case 'zh-CN':
        return 'Simplified Chinese';
      default:
        return '-';
    }
  };

  if (Array.isArray(code)) {
    let langString = code.map(langCode => langToString(langCode));
    return langString.join(', ');
  } else {
    return langToString(code);
  }
};

/**
 * Get age based on the birth date
 * @param {String} birthDate the birth date
 */
export const getAge = birthDate => {
  const today = dayjs();
  return today.diff(dayjs(birthDate), 'years');
};

/**
 * Get elapsed time in minutes
 * @param {Number} elapsed timestamp
 */
export const getElapsedMinutes = elapsed => {
  if (!elapsed) return '?';

  const today = dayjs();
  return today.diff(dayjs(elapsed), 'minutes');
};

/**
 * Get simple description of device object
 * @param {Object} deviceInfo the device object
 */
export const getDeviceInfo = deviceInfo => {
  if (!deviceInfo) return null;

  const { ios, android } = deviceInfo;
  const unknown = { brand: '-', systemVersion: '' };
  const { brand, systemVersion } = ios || android || unknown;

  return `${brand} ${systemVersion}`;
};

/**
 * Format the guest count into descriptive text
 */
export const formatGuestCount = booking => {
  const { adultCount, childCount } = booking;
  const counts = [
    { label: adultCount > 1 ? 'Adults' : 'Adult', count: adultCount },
    { label: childCount > 1 ? 'Children' : 'Child', count: childCount }
  ];

  return counts
    .filter(({ count }) => count > 0)
    .map(({ count, label }) => `${count} ${label}`)
    .join(', ');
};

export const capitalize = string => {
  if (!string) return '';

  string = string.toString();
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const filters = {
  formatTime,
  formatDateTime,
  formatFullDateTime,
  formatLangCode,
  formatGuestCount,
  getElapsedMinutes,
  capitalize
};

Object.keys(filters).forEach(key => {
  Vue.filter(key, filters[key]);
});
